import { useState } from "react";
import type { Rating } from "../lib/types.js";
import type { OutputRatingState } from "../lib/ratingState.js";

// Thumbs + optional comment + (for multi-variant jobs) "Choose" + "Accept".
// The parent owns the optimistic OutputRatingState and the network calls; this
// component is presentational plus a little local text-entry state.

const thumbBase = "rounded-md border px-2 py-1 text-sm transition-colors";
const thumbIdle =
  "border-gray-300 text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800";
const thumbUp = "border-green-500 bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-200";
const thumbDown = "border-red-500 bg-red-50 text-red-700 dark:bg-red-900 dark:text-red-200";

const btnGhost =
  "rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 " +
  "dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800";

const btnPrimary =
  "rounded-md bg-(--accent) px-3 py-1 text-xs font-medium text-(--accent-contrast) " +
  "hover:bg-(--accent-hover) disabled:opacity-50";

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

export function RatingControls({
  state,
  hasVariants,
  isAccepted,
  busy,
  onRate,
  onCommentBlur,
  onChoose,
  onAccept,
}: {
  state: OutputRatingState;
  hasVariants: boolean;
  isAccepted: boolean;
  busy?: boolean;
  onRate: (rating: Rating) => void;
  onCommentBlur: (comment: string) => void;
  onChoose: (reason: string) => void;
  onAccept: () => void;
}) {
  const [comment, setComment] = useState(state.comment);
  const [showComment, setShowComment] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [reason, setReason] = useState(state.chosenReason ?? "");

  const commentOpen = showComment || state.rating === "down" || comment.length > 0;

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy}
          className={`${thumbBase} ${state.rating === "up" ? thumbUp : thumbIdle}`}
          onClick={() => onRate("up")}
        >
          &#9650; Up
        </button>
        <button
          type="button"
          disabled={busy}
          className={`${thumbBase} ${state.rating === "down" ? thumbDown : thumbIdle}`}
          onClick={() => onRate("down")}
        >
          &#9660; Down
        </button>

        {!commentOpen && (
          <button type="button" className={btnGhost} onClick={() => setShowComment(true)}>
            Add comment
          </button>
        )}

        {hasVariants && !state.chosen && !choosing && (
          <button type="button" className={btnGhost} onClick={() => setChoosing(true)}>
            Choose
          </button>
        )}
        {hasVariants && state.chosen && (
          <span className="rounded bg-(--accent) px-1.5 py-0.5 text-xs text-(--accent-contrast)">
            Chosen{state.chosenReason ? `: ${state.chosenReason}` : ""}
          </span>
        )}

        <button type="button" className={btnPrimary} disabled={busy || isAccepted} onClick={onAccept}>
          {isAccepted ? "Accepted" : "Accept"}
        </button>
      </div>

      {commentOpen && (
        <textarea
          rows={2}
          className={inputCls}
          placeholder="Why? (feeds the next regenerate)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => {
            if (comment !== state.comment) onCommentBlur(comment);
          }}
        />
      )}

      {choosing && (
        <div className="flex gap-2">
          <input
            className={inputCls}
            placeholder="Reason this variant won (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <button
            type="button"
            className={btnPrimary}
            disabled={busy}
            onClick={() => {
              onChoose(reason);
              setChoosing(false);
            }}
          >
            Confirm
          </button>
          <button type="button" className={btnGhost} onClick={() => setChoosing(false)}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
